import React, { useContext, useEffect, useRef, useState } from "react";

import ReactDOMServer from "react-dom/server";

import html2canvas from "html2canvas";

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import { ArrowForwardIos, Email, Info } from "@mui/icons-material";

import { DownloadIcon } from "lucide-react";

import SignatureCanvas from "react-signature-canvas";

import JsPDF from "jspdf";

import Swal from "sweetalert2";

import api from "../../../api/axios";

import { b64DecodeUnicode } from "../../../Components/helpers/Helpers";

import { PermissionContext } from "../../../Components/Context/Context";

import { CustomButtonPlusIcon } from "../../../Components/Common/Design/CustomButtonPlusIcon";

import logoFidelitis from "../../../assets/logos/companies/fidelitis/logo_fidelitis.png";
import logoDerechosDelTrabajo from "../../../assets/logos/companies/fidelitis/derechos_del_trabajo.png";

import logoReclamatodo from "../../../assets/logos/companies/reclamatodo/logo-reclamatodo.png";

import logoPeritos from "../../../assets/logos/companies/peritos/logo_peritos.png";

import logoPresunto from "../../../assets/logos/companies/presuntoinocente/logo_pi.png";

import logoArgus from "../../../assets/logos/asociados/argus_social.png";

import logoGroup from "../../../assets/logos/asociados/grupo_fidelitis.png";

import ContractHeader from "./ContractHeader";

const DOCUMENT_FONT = "times";

const DOCUMENT_FONT_COLOR = "rgb(100, 116, 139)";

const DEFAULT_LOGO_OPACITY = 0.4;

const DEFAULT_PDF_SIZE = "letter";

const COMPANY_LOGOS = {
  1: {
    name: "Fidelitis",
    logo: logoFidelitis,
  },
  6: {
    name: "Peritos",
    logo: logoPeritos,
  },
  9: {
    name: "Reclamatodo",
    logo: logoReclamatodo,
  },
  12: {
    name: "Presunto Inocente",
    logo: logoPresunto,
  },
};

const DEFAULT_COMPANY = {
  name: "Fidelitis",
  logo: logoFidelitis,
};

const CASE_LOGO_CONFIG = {
  reclamatodo: {
    left: logoGroup,
    center: null,
    right: logoReclamatodo,
  },

  "presunto inocente": {
    left: logoGroup,
    center: null,
    right: logoPresunto,
  },

  fidelitis: {
    left: logoGroup,
    center: logoArgus,
    right: logoFidelitis,
  },

  "he dpp": {
    left: logoArgus,
    center: null,
    right: logoPresunto,
  },

  "derechos del trabajo": {
    left: logoGroup,
    center: null,
    right: logoDerechosDelTrabajo,
  },
};

const signStyles = {
  boxShadow: "0 6px 14px rgba(15, 23, 42, 0.24)",
  borderRadius: "10px",
  width: "100%",
};

const FirmaContent = ({
  caseContract,
  flagAcceptReject,
  acceptedContract,
  nextTask,
  rerender = "firma-0",
  activeTask,
  setUpdatedCase,
  updatedCase,
  dataCase,
  usersFlag,
  glassSurface,
  viewMode,
  contactConditionIfExist,
  requireContractAssignment,
  contractsList = [],
  newContract,
  contractInputValue,
  setNewContract,
  setContractInputValue,
  handleConfirmNewContract,
}) => {
  // CONTEXT / THEME
  const { can } = useContext(PermissionContext);

  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const isXs = useMediaQuery(theme.breakpoints.only("xs"));

  const isSm = useMediaQuery(theme.breakpoints.only("sm"));

  const isMd = useMediaQuery(theme.breakpoints.only("md"));

  const isLg = useMediaQuery(theme.breakpoints.only("lg"));

  const isXl = useMediaQuery(theme.breakpoints.up("xl"));

  // ========================================================
  // TIPOGRAFÍA DEL DOCUMENTO
  // ========================================================

  const FONT_FAMILY = "times";

  // REF DEL HEADER
  const contractHeaderRef = useRef(null);

  // VIEW MODE
  const resolvedViewMode =
    viewMode || (usersFlag ? "lawyer-flow" : "client-direct");

  const isLawyerFlow = resolvedViewMode === "lawyer-flow";

  const canSign = can("cases", "signcontract");

  const signatureWidth = isMobile ? 280 : isLawyerFlow ? 460 : 520;

  const signatureHeight = isMobile ? 150 : 200;

  // COMPANY
  const companyId = Number(dataCase?.company_relation?.id);

  const companyConfig = COMPANY_LOGOS[companyId] || DEFAULT_COMPANY;

  const companyName = companyConfig.name;

  const companyLogo = companyConfig.logo;

  // STATE
  const [acceptTermsAndConditions, setAcceptTermsAndConditions] =
    useState(false);

  const [rejectTermsAndConditions, setRejectTermsAndConditions] =
    useState(false);

  const [marketing, setMarketing] = useState(false);

  const [acceptedHtml, setAcceptedHtml] = useState(acceptedContract || "");

  useEffect(() => {
    if (caseContract?.firmaUsuario?.firma) {
      const savedContract = decodeBase64Unicode(
        caseContract.firmaUsuario.firma,
      );

      if (savedContract) {
        setAcceptedHtml(savedContract);
        return;
      }
    }

    setAcceptedHtml(acceptedContract || "");
  }, [acceptedContract, caseContract?.firmaUsuario?.firma]);

  // SIGNATURE
  const [signatureMode, setSignatureMode] = useState("canvas");

  const [showSignature, setShowSignature] = useState(false);

  const [signaturePad, setSignaturePad] = useState(null);

  const [signatureImage, setSignatureImage] = useState("");

  const [uploadedSignature, setUploadedSignature] = useState("");

  // PDF
  const [pdfPaperSize, setPdfPaperSize] = useState(DEFAULT_PDF_SIZE);

  // OTP
  const [email, setEmail] = useState(dataCase?.clients_relation?.email || "");

  const [openOtpDialog, setOpenOtpDialog] = useState(false);

  // DERIVED VALUES
  const canDownload =
    !!caseContract?.firmaUsuario?.firma ||
    !!signatureImage ||
    !!uploadedSignature;

  const isFirmaPending = activeTask?.status === 0;

  // TASK HELPERS
  const getTaskOrderById = (taskId) => {
    const flow = (dataCase?.completeFlow || []).find(
      (item) => Number(item?.id) === Number(taskId),
    );

    return Number(flow?.order || 0);
  };

  const activeTaskOrder = getTaskOrderById(activeTask?.task_id);

  const hasLaterPendingTask = (dataCase?.tasks || []).some(
    (task) =>
      Number(task?.status) === 0 &&
      getTaskOrderById(task?.task_id) > activeTaskOrder,
  );

  const shouldShowCompletePendingTaskButton =
    isFirmaPending && hasLaterPendingTask;

  // CONTRACT HELPERS
  const decodeBase64Unicode = (base64String = "") => {
    try {
      return b64DecodeUnicode(base64String);
    } catch (error) {
      console.error("Error decodificando contenido:", error);
      return "";
    }
  };

  const encodeBase64 = (value) => {
    return btoa(unescape(encodeURIComponent(value)));
  };

  const convertHtmlToText = (html = "") => {
    if (!html) return "";

    const parser = new DOMParser();
    const parsedHtml = parser.parseFromString(String(html), "text/html");

    const BLOCK_TAGS = new Set([
      "ADDRESS",
      "ARTICLE",
      "ASIDE",
      "BLOCKQUOTE",
      "DIV",
      "DL",
      "DT",
      "DD",
      "FIELDSET",
      "FIGCAPTION",
      "FIGURE",
      "FOOTER",
      "FORM",
      "H1",
      "H2",
      "H3",
      "H4",
      "H5",
      "H6",
      "HEADER",
      "HR",
      "LI",
      "MAIN",
      "NAV",
      "OL",
      "P",
      "PRE",
      "SECTION",
      "TABLE",
      "TBODY",
      "TD",
      "TFOOT",
      "TH",
      "THEAD",
      "TR",
      "UL",
    ]);

    const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT"]);

    const parts = [];

    const walk = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        parts.push(node.nodeValue || "");
        return;
      }

      if (node.nodeType !== Node.ELEMENT_NODE) {
        return;
      }

      const tagName = node.tagName.toUpperCase();

      if (SKIP_TAGS.has(tagName)) {
        return;
      }

      if (tagName === "BR") {
        parts.push("\n");
        return;
      }

      node.childNodes.forEach(walk);

      if (BLOCK_TAGS.has(tagName)) {
        parts.push("\n");
      }
    };

    parsedHtml.body.childNodes.forEach(walk);

    return parts
      .join("")
      .replace(/\u00a0/g, " ")
      .replace(/\u200B/g, "")
      .replace(/\r/g, "")
      .replace(/[ \t]+/g, " ")
      .replace(/[ \t]*\n[ \t]*/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .split("\n")
      .map((line) => line.trim())
      .filter((line, index, arr) => {
        return line !== "" || arr[index - 1] !== "";
      })
      .join("\n")
      .trim();
  };

  const getCaseTypeName = () => {
    return (
      convertHtmlToText(dataCase?.case_type_name || "") ||
      "Documento contractual"
    );
  };

  // LOGOS SEGÚN TIPO DE CASO
  const getCaseLogoConfig = () => {
    const normalizedCaseType = getCaseTypeName()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .toLowerCase();

    const configKey = Object.keys(CASE_LOGO_CONFIG).find((key) =>
      normalizedCaseType.includes(key),
    );

    if (Number(companyId) == 9) {
      return (
        CASE_LOGO_CONFIG["reclamatodo"] || {
          left: logoGroup,
          center: null,
          right: companyLogo,
        }
      );
    }
    if (Number(companyId) == 12) {
      return (
        CASE_LOGO_CONFIG["presunto inocente"] || {
          left: logoGroup,
          center: logoGroup,
          right: companyLogo,
        }
      );
    }

    if (Number(companyId) == 1) {
      return (
        CASE_LOGO_CONFIG[configKey] || {
          left: logoGroup,
          center: null,
          right: companyLogo,
        }
      );
    }

    return {
      left: logoGroup,
      center: null,
      right: companyLogo,
    };
  };

  const getContractTitle = () => {
    const titleFromContract =
      caseContract?.contractTitle ||
      caseContract?.title ||
      caseContract?.contract_title ||
      "";

    const cleanedTitle = convertHtmlToText(titleFromContract || "").trim();

    if (cleanedTitle) {
      return cleanedTitle;
    }

    const effectiveHtml = getEffectiveContractHtml();

    if (effectiveHtml && String(effectiveHtml).trim()) {
      return "Contrato";
    }

    return "Contrato";
  };

  const cleanContractHtml = (html = "") => {
    if (!html) return "";

    const parser = new DOMParser();
    const parsedHtml = parser.parseFromString(String(html), "text/html");

    const contractTitle = convertHtmlToText(getContractTitle());
    const normalizedTitle = contractTitle
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();

    const removeFollowingSeparators = (node) => {
      if (!node) return;

      let nextSibling = node.nextElementSibling;

      while (nextSibling) {
        const tagName = (nextSibling.tagName || "").toLowerCase();
        const nextText = convertHtmlToText(nextSibling.textContent || "")
          .replace(/\s+/g, " ")
          .trim();

        if (tagName === "hr" || !nextText) {
          const siblingToRemove = nextSibling;
          nextSibling = nextSibling.nextElementSibling;
          siblingToRemove.remove();
          continue;
        }

        break;
      }
    };

    // --------------------------------------------------------
    // Solo eliminamos un elemento si ÉL MISMO es el título.
    //
    // Nunca eliminamos un <div>, <section>, etc. completo solo
    // porque dentro contenga el título. Algunas empresas
    // almacenan el título junto con el primer párrafo dentro
    // del mismo contenedor.
    // --------------------------------------------------------
    const headings = parsedHtml.body.querySelectorAll("h1,h2,h3,h4,h5,h6");

    headings.forEach((heading) => {
      const headingText = convertHtmlToText(heading.textContent || "")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();

      if (normalizedTitle && headingText === normalizedTitle) {
        removeFollowingSeparators(heading);
        heading.remove();
      }
    });

    // --------------------------------------------------------
    // Si el primer elemento es un <p> cuyo contenido completo
    // es el título, podemos eliminar únicamente ese <p>.
    //
    // IMPORTANTE:
    // No hacemos firstElement.remove() indiscriminadamente.
    // --------------------------------------------------------
    const firstElement = parsedHtml.body.firstElementChild;

    if (firstElement) {
      const tagName = firstElement.tagName.toLowerCase();

      if (["p", "h1", "h2", "h3", "h4", "h5", "h6"].includes(tagName)) {
        const firstText = convertHtmlToText(firstElement.textContent || "")
          .replace(/\s+/g, " ")
          .trim()
          .toLowerCase();

        if (normalizedTitle && firstText === normalizedTitle) {
          removeFollowingSeparators(firstElement);
          firstElement.remove();
        }
      }
    }

    // --------------------------------------------------------
    // Eliminamos separadores que queden al inicio del contenido,
    // ya que el documento ya tiene su propia línea divisoria en el
    // encabezado y no queremos duplicarla.
    // --------------------------------------------------------
    let leadingNode = parsedHtml.body.firstElementChild;

    while (leadingNode) {
      const tagName = (leadingNode.tagName || "").toLowerCase();
      const leadingText = convertHtmlToText(leadingNode.textContent || "")
        .replace(/\s+/g, " ")
        .trim();

      if (tagName === "hr" || !leadingText) {
        const nextNode = leadingNode.nextElementSibling;
        leadingNode.remove();
        leadingNode = nextNode;
        continue;
      }

      break;
    }

    return parsedHtml.body.innerHTML;
  };

  // ACCEPTANCE TEXT// ACCEPTANCE TEXT
  const GetTextAcceptContract = ({
    clientName,
    clientDocument,
    companyName,
    ip,
  }) => {
    const date = new Date();

    const dateFormatted = `${date.getDate().toString().padStart(2, "0")}/${(
      date.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}/${date.getFullYear()}`;

    const timeFormatted = `${date.getHours().toString().padStart(2, "0")}:${date
      .getMinutes()
      .toString()
      .padStart(2, "0")}`;

    return (
      <div
        style={{
          fontFamily: DOCUMENT_FONT,
          fontSize: "13px",
          lineHeight: 1.6,
          color: "#2d3748",
        }}
      >
        <p>
          YO <strong>{clientName}</strong> CON NIF/PASAPORTE{" "}
          <strong>{clientDocument}</strong> ACEPTO EXPRESAMENTE LAS CONDICIONES
          GENERALES DE LA HOJA DE ENCARGO DE <strong>{companyName}</strong>
        </p>

        <p>
          HAGO CONSTAR ESTAR CONFORME CON LAS CONDICIONES DE ESTA HOJA DE
          ENCARGO CON FECHA <strong>{dateFormatted}</strong> A LAS{" "}
          <strong>{timeFormatted}</strong> DESDE LA IP <strong>{ip}</strong>
        </p>
      </div>
    );
  };

  // CONTRACT PREVIEW
  const ContractText = ({ accept, contract, includeTitle = true }) => {
    const caseTypeName = getCaseTypeName();

    const contractTitle = getContractTitle();
    const hasAcceptedContract = Boolean(accept && String(accept).trim());
    const hasExplicitContractTitle = Boolean(
      convertHtmlToText(
        caseContract?.contractTitle ||
          caseContract?.title ||
          caseContract?.contract_title ||
          "",
      ).trim(),
    );
    const shouldRenderHeader =
      includeTitle && (!hasAcceptedContract || hasExplicitContractTitle);
    const contractHtml = cleanContractHtml(
      hasAcceptedContract ? accept : contract?.contract || "",
    );

    return (
      <Box
        sx={{
          fontFamily: DOCUMENT_FONT,
          color: DOCUMENT_FONT_COLOR,

          backgroundColor: "#fff",
          width: "100%",
        }}
      >
        {shouldRenderHeader && (
          <Box
            sx={{
              textAlign: "center",
              mb: {
                xs: 2.5,
                sm: 3,
              },
            }}
          >
            <Typography
              component="div"
              sx={{
                fontFamily: DOCUMENT_FONT,
                fontSize: {
                  xs: "11px",
                  sm: "18px",
                },
                fontWeight: 700,
                letterSpacing: "0.08em",
                color: DOCUMENT_FONT_COLOR,

                textTransform: "uppercase",
                lineHeight: 1.3,
                mb: 0.8,
              }}
            >
              Documento contractual
            </Typography>

            <Typography
              component="div"
              sx={{
                fontFamily: DOCUMENT_FONT,
                fontSize: {
                  xs: "15px",
                  sm: "18px",
                },
                fontWeight: 400,
                color: DOCUMENT_FONT_COLOR,

                lineHeight: 1.4,
                mb: 0.7,
              }}
            >
              {caseTypeName}
            </Typography>

            <Box
              sx={{
                height: "1px",
                backgroundColor: "#cbd5e1",
                width: "100%",
                mt: 2,
              }}
            />

            <Typography
              component="div"
              sx={{
                fontFamily: DOCUMENT_FONT,
                fontSize: {
                  xs: "15px",
                  sm: "18px",
                },
                fontWeight: 700,
                color: DOCUMENT_FONT_COLOR,
                lineHeight: 1.3,
                textAlign: "left",
                mt: 2,
              }}
            >
              {contractTitle}
            </Typography>
          </Box>
        )}

        {hasAcceptedContract ? (
          <Box
            sx={{
              fontFamily: DOCUMENT_FONT,
              fontSize: {
                xs: "13px",
                sm: "14px",
              },
              lineHeight: 1.65,
              color: "#2d3748",
              textAlign: "justify",

              "& p": {
                fontFamily: DOCUMENT_FONT,
                fontSize: "inherit",
                lineHeight: "inherit",
                marginTop: "0.8em",
                marginBottom: "0.8em",
                textAlign: "justify",
              },

              "& h1, & h2, & h3, & h4, & h5, & h6": {
                fontFamily: DOCUMENT_FONT,
              },
            }}
            dangerouslySetInnerHTML={{
              __html: contractHtml,
            }}
          />
        ) : (
          <Box>
            <Box
              sx={{
                fontFamily: DOCUMENT_FONT,
                fontSize: {
                  xs: "13px",
                  sm: "14px",
                },
                lineHeight: 1.65,
                color: "#2d3748",
                textAlign: "justify",

                "& p": {
                  fontFamily: DOCUMENT_FONT,
                  fontSize: "inherit",
                  lineHeight: "inherit",
                  marginTop: "0.8em",
                  marginBottom: "0.8em",
                  textAlign: "justify",
                },

                "& h1, & h2, & h3, & h4, & h5, & h6": {
                  fontFamily: DOCUMENT_FONT,
                },
              }}
              dangerouslySetInnerHTML={{
                __html: contractHtml,
              }}
            />

            <Box
              sx={{
                height: "1px",
                backgroundColor: "#cbd5e1",
                mt: 3,
                mb: 2.5,
              }}
            />

            <GetTextAcceptContract
              clientName={contract?.contractAcceptData?.client_name ?? ""}
              clientDocument={contract?.contractAcceptData?.client_nif ?? ""}
              companyName={
                contract?.contractAcceptData?.company_name ?? companyName
              }
              ip={contract?.contractAcceptData?.client_ip ?? ""}
            />
          </Box>
        )}
      </Box>
    );
  };

  const getEffectiveContractHtml = () => {
    const fromAcceptedState = acceptedHtml && String(acceptedHtml).trim();

    if (fromAcceptedState) {
      return fromAcceptedState;
    }

    const fromSavedConfig = caseContract?.firmaUsuario?.firma;

    if (fromSavedConfig) {
      const decoded = decodeBase64Unicode(fromSavedConfig);

      if (decoded && String(decoded).trim()) {
        return decoded;
      }
    }

    return caseContract?.contract || "";
  };

  // CONTRACT HTML
  const getContractHTML = () => {
    return ReactDOMServer.renderToStaticMarkup(
      <ContractText
        accept={getEffectiveContractHtml()}
        contract={caseContract}
        includeTitle={false}
      />,
    );
  };

  // CONVERTIR CONTRACT HEADER A IMAGEN
  const getContractHeaderImage = async () => {
    if (!contractHeaderRef.current) {
      throw new Error("No se encontró el encabezado del contrato.");
    }

    const canvas = await html2canvas(contractHeaderRef.current, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: "#ffffff",
      logging: false,
    });

    return {
      image: canvas.toDataURL("image/png"),
      width: canvas.width,
      height: canvas.height,
    };
  };

  // SIGNATURE HANDLERS
  const clearSignature = () => {
    if (signaturePad) {
      signaturePad.clear();
    }

    setSignatureImage("");

    setUploadedSignature("");
  };

  const saveSignature = async (flag) => {
    if (!signaturePad || signaturePad.isEmpty()) {
      Swal.fire({
        title: "Sin firma",
        text: "Por favor realiza la firma antes de continuar.",
        icon: "warning",
        confirmButtonColor: "#1ab394",
      });

      return;
    }

    const dataURL = signaturePad.getCanvas().toDataURL("image/png");

    if (acceptedHtml && dataURL && flag === "signed") {
      try {
        Swal.fire({
          title: "Guardando...",
          text: "Por favor espera",
          allowOutsideClick: false,
          allowEscapeKey: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        const base64 = encodeBase64(acceptedHtml);

        const config = {
          firma: base64,
          firma_usuario: dataURL ?? "",
        };

        const formDataTask = new FormData();

        formDataTask.append("current_task", activeTask?.id || "");

        formDataTask.append("config", JSON.stringify(config));

        const url = `${process.env.REACT_APP_API_URL}/casestasks/updatetasks`;

        await api.post(url, formDataTask);

        setSignatureImage(dataURL);

        setUploadedSignature(dataURL);

        Swal.close();

        Swal.fire({
          title: "Guardado",
          text: "La firma se ha guardado en el documento exitosamente.",
          icon: "success",
          confirmButtonColor: "#1ab394",
          allowEscapeKey: false,
          allowOutsideClick: false,
        }).then(() => {
          setUpdatedCase((prev) => !prev);
        });
      } catch (error) {
        console.error(error);

        Swal.close();

        Swal.fire({
          title: "Error",
          text: "No se pudo guardar la firma. Intenta nuevamente.",
          icon: "error",
          confirmButtonColor: "#d33",
        });
      }
    } else {
      setSignatureImage(dataURL);

      setUploadedSignature(dataURL);

      Swal.fire({
        title: "Firma guardada",
        text: "Puede continuar con el proceso.",
        icon: "success",
        confirmButtonColor: "#1ab394",
      });
    }
  };

  // CONTRACT DECISION
  const handleAcceptChange = () => {
    setAcceptTermsAndConditions(true);

    setRejectTermsAndConditions(false);
  };

  const handleRejectChange = () => {
    setRejectTermsAndConditions(true);

    setAcceptTermsAndConditions(false);
  };

  const handleContractDecision = () => {
    const contractHtml = getContractHTML();

    if (acceptTermsAndConditions) {
      const base64 = encodeBase64(contractHtml);

      let dataURL;

      if (caseContract?.firmaUsuario?.firma_usuario) {
        dataURL = caseContract.firmaUsuario.firma_usuario;
      } else if (signatureImage) {
        dataURL = signatureImage;
      } else if (signaturePad && !signaturePad.isEmpty()) {
        dataURL = signaturePad.getCanvas().toDataURL("image/png");
      } else {
        dataURL = "";
      }

      const config = {
        firma: base64,
        firma_usuario: dataURL ?? "",
      };

      Swal.fire({
        title: "¿Estás seguro que deseas aceptar la firma?",
        icon: "warning",
        showDenyButton: true,
        confirmButtonColor: "#1ab394",
        confirmButtonText: "Sí",
        denyButtonText: "Volver",
        cancelButtonColor: "#d33",
        reverseButtons: true,
      }).then((result) => {
        if (result.isConfirmed) {
          nextTask(config, 1, "next", "");
        }
      });
    } else if (rejectTermsAndConditions) {
      nextTask({}, 2, true, "");
    } else {
      Swal.fire({
        title: "¡Error!",
        text: "Por favor marque la casilla",
        showDenyButton: false,
        confirmButtonColor: "#1ab394",
        confirmButtonText: "Continuar",
      });
    }
  };

  // JUSTIFICAR TEXTO EN jsPDF
  const drawJustifiedText = ({
    doc,
    text,
    x,
    y,
    maxWidth,
    lineHeight,
    pageHeight,
    marginBottom,
    onNewPage,
    fontSize = 10.5,
    color = [45, 55, 72],
  }) => {
    if (!text) {
      return y;
    }

    doc.setFont(FONT_FAMILY, "normal");
    doc.setFontSize(fontSize);
    doc.setTextColor(...color);

    const normalizedText = String(text)
      .replace(/\u00a0/g, " ")
      .replace(/\u200B/g, "")
      .replace(/\r\n/g, "\n")
      .replace(/\r/g, "\n");

    const spaceWidth = doc.getTextWidth(" ");

    // --------------------------------------------------------
    // Divide el contenido por saltos reales.
    // Esto evita que <br> o estructuras HTML de algunas
    // empresas terminen pegando frases diferentes.
    // --------------------------------------------------------
    const paragraphs = normalizedText
      .split(/\n+/)
      .map((value) => value.trim())
      .filter(Boolean);

    const drawLine = (lineWords, shouldJustify = false) => {
      if (!lineWords.length) {
        return;
      }

      if (y > pageHeight - marginBottom) {
        y = onNewPage();

        doc.setFont(FONT_FAMILY, "normal");
        doc.setFontSize(fontSize);
        doc.setTextColor(...color);
      }

      const lineText = lineWords.join(" ");
      const lineWidth = doc.getTextWidth(lineText);

      if (!shouldJustify || lineWords.length <= 1 || lineWidth >= maxWidth) {
        doc.text(lineText, x, y);
        y += lineHeight;
        return;
      }

      const gaps = lineWords.length - 1;
      const extraSpace = (maxWidth - lineWidth) / gaps;

      let cursorX = x;

      lineWords.forEach((word, index) => {
        doc.text(word, cursorX, y);

        const wordWidth = doc.getTextWidth(word);

        if (index < lineWords.length - 1) {
          cursorX += wordWidth + spaceWidth + extraSpace;
        }
      });

      y += lineHeight;
    };

    // --------------------------------------------------------
    // Divide una palabra demasiado larga.
    //
    // Evita que URLs, identificadores, números de expediente
    // u otras cadenas largas desaparezcan por superar el ancho.
    // --------------------------------------------------------
    const splitLongWord = (word) => {
      if (doc.getTextWidth(word) <= maxWidth) {
        return [word];
      }

      const chunks = [];
      let current = "";

      for (const character of word) {
        const candidate = current + character;

        if (current && doc.getTextWidth(candidate) > maxWidth) {
          chunks.push(current);
          current = character;
        } else {
          current = candidate;
        }
      }

      if (current) {
        chunks.push(current);
      }

      return chunks;
    };

    paragraphs.forEach((paragraph) => {
      const rawWords = paragraph.split(/\s+/).filter(Boolean);

      const words = rawWords.flatMap(splitLongWord);

      let currentLine = [];
      let currentWidth = 0;

      words.forEach((word, index) => {
        const wordWidth = doc.getTextWidth(word);

        const requiredWidth =
          currentLine.length === 0
            ? wordWidth
            : currentWidth + spaceWidth + wordWidth;

        if (currentLine.length > 0 && requiredWidth > maxWidth) {
          // Las líneas intermedias se justifican.
          drawLine(currentLine, true);

          currentLine = [];
          currentWidth = 0;
        }

        currentLine.push(word);

        currentWidth =
          currentLine.length === 1
            ? wordWidth
            : currentWidth + spaceWidth + wordWidth;

        // La última línea del párrafo nunca se justifica.
        if (index === words.length - 1) {
          drawLine(currentLine, false);

          currentLine = [];
          currentWidth = 0;
        }
      });

      // Espacio discreto entre párrafos.
      y += 2;
    });

    return y;
  };

  // OBTENER PÁRRAFOS DEL HTML// OBTENER PÁRRAFOS DEL HTML
  const getContractParagraphs = (html = "") => {
    if (!html) {
      return [];
    }

    const parser = new DOMParser();
    const parsedHtml = parser.parseFromString(String(html), "text/html");

    const paragraphs = [];

    const BLOCK_TAGS = new Set(["P", "LI", "H1", "H2", "H3", "H4", "H5", "H6"]);

    const CONTAINER_TAGS = new Set([
      "DIV",
      "SECTION",
      "ARTICLE",
      "MAIN",
      "HEADER",
      "FOOTER",
      "BLOCKQUOTE",
      "TD",
      "TH",
    ]);

    const INLINE_OR_TEXT_TAGS = new Set([
      "SPAN",
      "STRONG",
      "B",
      "EM",
      "I",
      "U",
      "S",
      "A",
      "SMALL",
      "SUB",
      "SUP",
      "MARK",
    ]);

    const addBlock = (element) => {
      const tag = element.tagName.toLowerCase();

      const text = convertHtmlToText(element.innerHTML || "").trim();

      if (!text) {
        return;
      }

      paragraphs.push({
        text,
        type: BLOCK_TAGS.has(element.tagName.toUpperCase()) ? tag : "p",
      });
    };

    const walkContainer = (container) => {
      const childNodes = Array.from(container.childNodes);

      let looseText = [];

      const flushLooseText = () => {
        const value = looseText.join(" ").replace(/\s+/g, " ").trim();

        if (value) {
          paragraphs.push({
            text: value,
            type: "p",
          });
        }

        looseText = [];
      };

      childNodes.forEach((node) => {
        // Texto directamente dentro del contenedor.
        if (node.nodeType === Node.TEXT_NODE) {
          const value = (node.nodeValue || "").trim();

          if (value) {
            looseText.push(value);
          }

          return;
        }

        if (node.nodeType !== Node.ELEMENT_NODE) {
          return;
        }

        const tag = node.tagName.toUpperCase();

        if (BLOCK_TAGS.has(tag)) {
          flushLooseText();
          addBlock(node);
          return;
        }

        if (CONTAINER_TAGS.has(tag)) {
          flushLooseText();
          walkContainer(node);
          return;
        }

        if (tag === "BR") {
          flushLooseText();
          return;
        }

        // Elementos inline que no son bloques.
        if (INLINE_OR_TEXT_TAGS.has(tag)) {
          const value = convertHtmlToText(node.outerHTML || "").trim();

          if (value) {
            looseText.push(value);
          }
        }
      });

      flushLooseText();
    };

    walkContainer(parsedHtml.body);

    // --------------------------------------------------------
    // TABLAS
    //
    // Algunas plantillas contractuales utilizan tablas para
    // datos o cláusulas. Si no fueron capturadas como bloques,
    // recuperamos sus filas como párrafos.
    // --------------------------------------------------------
    if (!paragraphs.length) {
      parsedHtml.body.querySelectorAll("tr").forEach((row) => {
        const rowText = convertHtmlToText(row.innerHTML || "").trim();

        if (rowText) {
          paragraphs.push({
            text: rowText,
            type: "p",
          });
        }
      });
    }

    // --------------------------------------------------------
    // FALLBACK FINAL
    //
    // Nunca dejamos el PDF vacío si el HTML contiene texto.
    // --------------------------------------------------------
    if (!paragraphs.length) {
      const fallbackText = convertHtmlToText(parsedHtml.body.innerHTML);

      if (fallbackText) {
        return fallbackText
          .split(/\n{2,}/)
          .map((value) => value.trim())
          .filter(Boolean)
          .map((value) => ({
            text: value,
            type: "p",
          }));
      }
    }

    return paragraphs;
  };

  const downloadContract = async () => {
    Swal.fire({
      title: "Generando PDF...",
      text: "Por favor espera un momento",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      // ========================================================
      // OBTENER HTML DEL CONTRATO
      // ========================================================

      const effectiveContractHtml = getEffectiveContractHtml();
      const htmlContent = effectiveContractHtml || getContractHTML();
      const cleanedHtml = cleanContractHtml(htmlContent);

      const contentText = convertHtmlToText(cleanedHtml);

      if (!contentText || contentText.length < 20) {
        throw new Error(
          "No se pudo obtener el contenido del contrato para el PDF.",
        );
      }

      // ========================================================
      // INFORMACIÓN DEL CONTRATO
      // ========================================================

      const caseTypeName = getCaseTypeName();
      const hasSavedContractContent = Boolean(
        effectiveContractHtml && String(effectiveContractHtml).trim(),
      );
      const contractTitle = getContractTitle();

      // ========================================================
      // CREAR DOCUMENTO PDF
      // ========================================================

      const doc = new JsPDF({
        orientation: "p",
        unit: "pt",
        format: pdfPaperSize,
        compress: true,
      });

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      // ========================================================
      // TIPOGRAFÍA
      // ========================================================
      //
      // Se utiliza Times en todo el documento para mantener
      // una apariencia formal y uniforme.
      //
      // ========================================================

      const FONT_FAMILY = "times";

      const FONT_SIZE = {
        title: 11.5,
        subtitle: 11,
        body: 10.5,
        h1: 14,
        h2: 12.5,
        h3: 11,
        headerLabel: 12,
        signature: 10,
        footer: 8,
        noSignature: 8.5,
      };

      // ========================================================
      // MÁRGENES
      // ========================================================

      const marginLeft = 58;
      const marginRight = 58;
      const marginTop = 12;
      const marginBottom = 42;

      const contentWidth = pageWidth - marginLeft - marginRight;

      // ========================================================
      // RESPONSIVE
      // ========================================================
      //
      // El PDF se adapta al viewport desde el cual se descarga.
      //
      // xs -> teléfonos
      // sm -> tablets pequeñas
      // md -> tablets / laptops
      // lg -> desktop
      // xl -> pantallas grandes
      //
      // ========================================================

      let PDF_CONTENT_OFFSET = -40;

      if (isXs) {
        PDF_CONTENT_OFFSET = -20;
      } else if (isSm) {
        PDF_CONTENT_OFFSET = -30;
      } else if (isMd) {
        PDF_CONTENT_OFFSET = -30;
      } else if (isLg) {
        PDF_CONTENT_OFFSET = -30;
      } else if (isXl) {
        PDF_CONTENT_OFFSET = -30;
      }

      // ========================================================
      // HEADER
      // ========================================================

      const headerData = await getContractHeaderImage();

      // ========================================================
      // HEADER PROPORCIONAL
      // ========================================================

      const sourceWidth = headerData.width;
      const sourceHeight = headerData.height;

      const headerAspectRatio = sourceWidth / sourceHeight;

      const pdfHeaderWidth = pageWidth;

      const pdfHeaderHeight = pdfHeaderWidth / headerAspectRatio;

      // ========================================================
      // INSERTAR HEADER
      // ========================================================

      doc.addImage(
        headerData.image,
        "PNG",
        0,
        0,
        pdfHeaderWidth,
        pdfHeaderHeight,
      );

      // ========================================================
      // CURSOR INICIAL
      // ========================================================

      let cursorY = pdfHeaderHeight + 40 + PDF_CONTENT_OFFSET;

      // ========================================================
      // TEXTO SUPERIOR
      // ========================================================

      doc.setFont(FONT_FAMILY, "bold");

      doc.setFontSize(FONT_SIZE.headerLabel);

      doc.setTextColor(100, 116, 139);

      doc.text("DOCUMENTO CONTRACTUAL", pageWidth / 2, cursorY, {
        align: "center",
      });

      cursorY += 25;

      // ========================================================
      // TIPO DE DOCUMENTO
      // ========================================================

      doc.setFont(FONT_FAMILY, "normal");

      doc.setFontSize(FONT_SIZE.subtitle);

      doc.setTextColor(100, 116, 139);

      const caseTypeLines = doc.splitTextToSize(
        caseTypeName,
        contentWidth - 40,
      );

      doc.text(caseTypeLines, pageWidth / 2, cursorY, {
        align: "center",
      });

      cursorY += caseTypeLines.length * 14 + 2;

      // ========================================================
      // LÍNEA SEPARADORA
      // ========================================================

      doc.setDrawColor(177, 201, 228);

      doc.setLineWidth(0.8);

      doc.line(marginLeft, cursorY, pageWidth - marginRight, cursorY);

      cursorY += 24;

      // ========================================================
      // TÍTULO PRINCIPAL (alineado a la izquierda)
      // ========================================================

      doc.setFont(FONT_FAMILY, "bold");
      doc.setFontSize(FONT_SIZE.title);
      doc.setTextColor(100, 116, 139);

      const titleLines = contractTitle
        ? doc.splitTextToSize(contractTitle, contentWidth)
        : [];

      if (titleLines.length > 0) {
        doc.text(titleLines, marginLeft, cursorY);
        cursorY += titleLines.length * 14 + 12;
      }

      // ========================================================
      // PÁRRAFOS DEL CONTRATO
      // ========================================================

      const contractParagraphs = getContractParagraphs(cleanedHtml);

      // ========================================================
      // FUNCIÓN PARA NUEVA PÁGINA
      // ========================================================

      const createInternalPage = () => {
        doc.addPage();

        let newCursorY = 20;

        // ------------------------------------------------------
        // TÍTULO PEQUEÑO DE CONTINUIDAD
        // ------------------------------------------------------

        doc.setFont(FONT_FAMILY, "bold");

        doc.setFontSize(8);

        doc.setTextColor(148, 163, 184);

        doc.text(contractTitle, marginLeft, newCursorY);

        newCursorY += 20;

        // ------------------------------------------------------
        // CONFIGURAR TEXTO
        // ------------------------------------------------------

        doc.setFont(FONT_FAMILY, "normal");

        doc.setFontSize(FONT_SIZE.body);

        doc.setTextColor(45, 55, 72);

        return newCursorY;
      };

      // ========================================================
      // DIBUJAR CADA PÁRRAFO
      // ========================================================

      contractParagraphs.forEach((paragraph, paragraphIndex) => {
        const paragraphType = paragraph.type;

        // ====================================================
        // HEADINGS
        // ====================================================

        if (["h1", "h2", "h3", "h4", "h5", "h6"].includes(paragraphType)) {
          if (cursorY > pageHeight - marginBottom - 50) {
            cursorY = createInternalPage();
          }

          // --------------------------------------------------
          // FUENTE DEL HEADING
          // --------------------------------------------------

          doc.setFont(FONT_FAMILY, "bold");

          // --------------------------------------------------
          // TAMAÑO SEGÚN NIVEL
          // --------------------------------------------------

          const headingSize =
            paragraphType === "h1"
              ? FONT_SIZE.h1
              : paragraphType === "h2"
                ? FONT_SIZE.h2
                : FONT_SIZE.h3;

          doc.setFontSize(headingSize);

          doc.setTextColor(15, 23, 42);

          // --------------------------------------------------
          // AJUSTAR TEXTO
          // --------------------------------------------------

          const headingLines = doc.splitTextToSize(
            paragraph.text,
            contentWidth,
          );

          // --------------------------------------------------
          // DIBUJAR HEADING
          // --------------------------------------------------

          doc.text(headingLines, marginLeft, cursorY);

          // --------------------------------------------------
          // ESPACIADO
          // --------------------------------------------------

          cursorY += headingLines.length * (headingSize + 5) + 10;

          return;
        }

        // ====================================================
        // PÁRRAFOS
        // ====================================================

        doc.setFont(FONT_FAMILY, "normal");

        doc.setFontSize(FONT_SIZE.body);

        doc.setTextColor(45, 55, 72);

        cursorY = drawJustifiedText({
          doc,
          text: paragraph.text,
          x: marginLeft,
          y: cursorY,
          maxWidth: contentWidth,

          // Interlineado formal
          lineHeight: 15,

          pageHeight,
          marginBottom: marginBottom + 30,

          fontSize: FONT_SIZE.body,

          color: [45, 55, 72],

          onNewPage: createInternalPage,
        });

        // ====================================================
        // ESPACIO ENTRE PÁRRAFOS
        // ====================================================

        cursorY += 5;

        // ====================================================
        // EVITAR PÁGINA EN BLANCO
        // ====================================================

        if (paragraphIndex < contractParagraphs.length - 1) {
          if (cursorY > pageHeight - marginBottom - 30) {
            cursorY = createInternalPage();
          }
        }
      });

      // ========================================================
      // BLOQUE DE FIRMA
      // ========================================================

      const signatureBlockHeight = 125;

      if (cursorY > pageHeight - marginBottom - signatureBlockHeight) {
        cursorY = createInternalPage();
      }

      cursorY += 14;

      // ========================================================
      // LÍNEA SUPERIOR DE FIRMA
      // ========================================================

      doc.setDrawColor(203, 213, 225);

      doc.setLineWidth(0.7);

      doc.line(marginLeft, cursorY, pageWidth - marginRight, cursorY);

      cursorY += 22;

      // ========================================================
      // TÍTULO DE FIRMA
      // ========================================================

      doc.setFont(FONT_FAMILY, "bold");

      doc.setFontSize(FONT_SIZE.signature);

      doc.setTextColor(15, 23, 42);

      doc.text("Firma del cliente", marginLeft, cursorY);

      cursorY += 10;

      // ========================================================
      // FIRMA
      // ========================================================

      const finalSignature =
        signatureImage || caseContract?.firmaUsuario?.firma_usuario || null;

      if (finalSignature && /^data:image\/.*$/.test(finalSignature)) {
        const imgFormat = finalSignature.includes("image/jpeg")
          ? "JPEG"
          : "PNG";

        const finalSignatureWidth = 180;
        const finalSignatureHeight = 75;

        doc.addImage(
          finalSignature,
          imgFormat,
          marginLeft,
          cursorY,
          finalSignatureWidth,
          finalSignatureHeight,
        );
      } else {
        doc.setFont(FONT_FAMILY, "normal");

        doc.setFontSize(FONT_SIZE.noSignature);

        doc.setTextColor(100, 116, 139);

        doc.text("No se registró firma.", marginLeft, cursorY + 25);
      }

      // ========================================================
      // NOMBRE SEGURO
      // ========================================================

      const safeClientName = (
        caseContract?.contractAcceptData?.client_name || "cliente"
      )
        .replace(/\s+/g, "_")
        .replace(/[^\w-]/g, "");

      // ========================================================
      // FOOTER
      // ========================================================

      const totalPages = doc.getNumberOfPages();

      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);

        // ------------------------------------------------------
        // LÍNEA DEL FOOTER
        // ------------------------------------------------------

        doc.setDrawColor(226, 232, 240);

        doc.setLineWidth(0.5);

        doc.line(
          marginLeft,
          pageHeight - 30,
          pageWidth - marginRight,
          pageHeight - 30,
        );

        // ------------------------------------------------------
        // TEXTO DEL FOOTER
        // ------------------------------------------------------

        doc.setFont(FONT_FAMILY, "normal");

        doc.setFontSize(FONT_SIZE.footer);

        doc.setTextColor(148, 163, 184);

        // ------------------------------------------------------
        // TEXTO IZQUIERDO
        // ------------------------------------------------------

        doc.text("Documento contractual", marginLeft, pageHeight - 16);

        // ------------------------------------------------------
        // NÚMERO DE PÁGINA
        // ------------------------------------------------------

        doc.text(
          `Página ${i} de ${totalPages}`,
          pageWidth - marginRight,
          pageHeight - 16,
          {
            align: "right",
          },
        );
      }

      // ========================================================
      // GUARDAR PDF
      // ========================================================

      doc.save(`Contrato-${safeClientName}-${pdfPaperSize}.pdf`);

      // ========================================================
      // ÉXITO
      // ========================================================

      Swal.fire({
        icon: "success",
        title: "PDF generado",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (e) {
      console.error("Error generando PDF:", e);

      Swal.fire({
        icon: "error",
        title: "Error al generar el PDF",
        text: e?.message || "No se pudo generar el documento.",
      });
    }
  };

  //
  // OTP
  //

  const handleCloseOtpDialog = () => {
    setOpenOtpDialog(false);
  };

  const sendOtp = async () => {
    Swal.fire({
      title: "Enviando código...",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      await api.post(`${process.env.REACT_APP_API_URL}/send-otp-client`, {
        email,
        case_id: dataCase?.id,
      });

      Swal.fire(
        "Código enviado",
        "El código ha sido enviado a correo del cliente",
        "success",
      );
    } catch (error) {
      console.error("Error enviando OTP:", error);

      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error?.response?.data?.message ||
          "No se pudo enviar el código. Verifica el correo.",
      });
    } finally {
      handleCloseOtpDialog();
    }
  };

  const handleSend = () => {
    if (!email) return;

    sendOtp();
  };

  //
  // COMPLETE PENDING TASK
  //

  const handleCompletePendingTask = () => {
    if (!activeTask?.id || !dataCase?.id) {
      return;
    }

    Swal.fire({
      title: "¿Dar por completada esta tarea?",
      text: "Solo se marcará la tarea actual como completada.",
      icon: "warning",
      showDenyButton: true,
      confirmButtonColor: "#1ab394",
      confirmButtonText: "Sí",
      reverseButtons: true,
      denyButtonText: "Volver",
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      try {
        await api.post(
          `${process.env.REACT_APP_API_URL}/casestasks/complete-pending-task`,
          {
            id: Number(activeTask.id),
            case_id: Number(dataCase.id),
          },
        );

        Swal.fire({
          title: "Guardado",
          text: "La tarea actual se completó correctamente.",
          icon: "success",
          confirmButtonColor: "#1ab394",
        });

        setUpdatedCase((prev) => !prev);
      } catch (error) {
        console.error("Error al completar tarea pendiente:", error);

        Swal.fire({
          title: "Error",
          text: "No se pudo completar la tarea actual.",
          icon: "error",
          confirmButtonColor: "#d33",
        });
      }
    });
  };

  //
  // EFFECTS
  //

  useEffect(() => {
    setAcceptedHtml(acceptedContract);
  }, [acceptedContract]);

  useEffect(() => {
    const savedSignature = caseContract?.firmaUsuario?.firma_usuario;

    if (savedSignature) {
      setUploadedSignature(savedSignature);

      setSignatureImage(savedSignature);
    }

    setSignatureMode("canvas");
  }, [caseContract]);

  const isFidelitis = companyConfig?.name === "Fidelitis";

  const leftLogoSizes = isFidelitis
    ? {
        xs: "24%",
        sm: "28%",
        md: "30%",
        lg: "50%",
        xl: "80%",
      }
    : companyName === "Reclamatodo"
      ? {
          xs: "24%",
          sm: "28%",
          md: "30%",
          lg: "32%",
          xl: "34%",
        }
      : {
          xs: "26%",
          sm: "30%",
          md: "32%",
          lg: "34%",
          xl: "38%",
        };

  const centerLogoSizes = isFidelitis
    ? {
        xs: "12%",
        sm: "14%",
        md: "16%",
        lg: "18%",
        xl: "20%",
      }
    : {
        xs: "12%",
        sm: "14%",
        md: "15%",
        lg: "16%",
        xl: "17%",
      };

  const rightLogoSizes = isFidelitis
    ? {
        xs: "18%",
        sm: "18%",
        md: "16%",
        lg: "20%",
        xl: "25%",
      }
    : companyName === "Reclamatodo"
      ? {
          xs: "24%",
          sm: "28%",
          md: "30%",
          lg: "32%",
          xl: "34%",
        }
      : companyName === "Peritos"
        ? {
            xs: "22%",
            sm: "24%",
            md: "26%",
            lg: "28%",
            xl: "30%",
          }
        : {
            xs: "22%",
            sm: "24%",
            md: "26%",
            lg: "28%",
            xl: "32%",
          };

  const rightLogoMargins = isFidelitis
    ? {
        xs: 1,
        sm: 1.5,
        md: 2,
        lg: 4,
        xl: 8,
      }
    : {
        xs: 1,
        sm: 1.5,
        md: 2,
        lg: 2.5,
        xl: 3,
      };

  //
  // RENDER
  //

  return (
    <Box
      key={rerender}
      sx={{
        ...signStyles,

        px: {
          xs: 1.25,
          sm: 2,
          md: isLawyerFlow ? 10 : 3,
        },
        py: {
          xs: 2,
        },

        width: "auto",

        mx: "auto",

        my: {
          xs: 0.5,
          md: 1.5,
        },

        fontFamily: DOCUMENT_FONT,
        color: DOCUMENT_FONT_COLOR,
      }}
    >
      {/* ======================================================
          OTP
          ====================================================== */}

      {requireContractAssignment && (
        <Box
          sx={{
            mb: 2.5,
            p: { xs: 2, sm: 2.5 },
            borderRadius: 3,
            border: "1px solid rgba(99, 102, 241, 0.18)",
            background:
              "linear-gradient(135deg, rgba(99,102,241,0.08), rgba(14,165,233,0.04))",
            boxShadow: "0 10px 24px rgba(15, 23, 42, 0.04)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
              mb: 2,
            }}
          >
            <Box>
              <Typography
                variant="overline"
                sx={{
                  display: "block",
                  fontWeight: 700,
                  letterSpacing: 1.2,
                  color: "#4f46e5",
                  mb: 0.5,
                }}
              >
                Contrato
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: 14, sm: 15 },
                  color: "#334155",
                  lineHeight: 1.5,
                }}
              >
                Este expediente no tiene un contrato asignado. Puede elegir uno
                aquí para continuar con el flujo.
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Autocomplete
              options={contractsList}
              value={newContract}
              inputValue={contractInputValue}
              onChange={(event, value) => setNewContract?.(value)}
              onInputChange={(event, value) => setContractInputValue?.(value)}
              getOptionLabel={(option) =>
                option?.contract_title || option?.title || "Contrato"
              }
              isOptionEqualToValue={(option, value) =>
                Number(option?.id || 0) === Number(value?.id || 0)
              }
              sx={{
                minWidth: { xs: "100%", sm: 360 },
                flex: 1,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  backgroundColor: "rgba(255,255,255,0.8)",
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Seleccionar contrato"
                  size="small"
                />
              )}
            />

            <Button
              variant="contained"
              disabled={!newContract}
              onClick={handleConfirmNewContract}
              sx={{
                borderRadius: 2,
                px: 2.25,
                py: 1,
                textTransform: "none",
                fontWeight: 600,
                boxShadow: "none",
                background: "linear-gradient(135deg, #4f46e5, #3b82f6)",
                "&:hover": {
                  background: "linear-gradient(135deg, #4338ca, #2563eb)",
                  boxShadow: "none",
                },
              }}
            >
              Asignar contrato
            </Button>
          </Box>
        </Box>
      )}

      {isLawyerFlow && (
        <Box
          sx={{
            marginBottom: {
              xs: "16px",
              sm: "24px",
            },

            display: "flex",

            justifyContent: "end",
          }}
        >
          <CustomButtonPlusIcon
            variant="outlined"
            color="success"
            position="end"
            size="small"
            icon={Email}
            handleFunction={() => setOpenOtpDialog(true)}
            text="Reenviar código de verificación al cliente"
          />
        </Box>
      )}

      {/* ======================================================
          CONTRACT HEADER + LOGOS
          ====================================================== */}

      <Box
        ref={contractHeaderRef}
        sx={{
          position: "relative",
          width: "100%",
          height: "250px",
          overflow: "hidden",
          flexShrink: 0,
        }}
      >
        {/* FONDO / ONDAS */}

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            zIndex: 1,
            pointerEvents: "none",
          }}
        >
          <ContractHeader />
        </Box>

        {/* LOGOS */}

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",

            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",

            px: {
              xs: 2,
              sm: 3,
            },

            boxSizing: "border-box",

            zIndex: 2,
            pointerEvents: "none",
          }}
        >
          {(() => {
            const logoConfig = getCaseLogoConfig();

            return (
              <>
                {/* LOGO IZQUIERDO */}

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",

                    width: leftLogoSizes,

                    maxWidth: {
                      xs: "160px",
                      sm: "200px",
                      md: "240px",
                      lg: "280px",
                      xl: "400px",
                    },

                    minWidth: 0,
                    flexShrink: 1,
                    ml: companyName === "Reclamatodo" ? 3 : "0",

                    position: "relative",
                    zIndex: 3,
                  }}
                >
                  <img
                    src={logoConfig.left}
                    alt="Logo izquierdo"
                    style={{
                      width: leftLogoSizes,

                      display: "block",

                      opacity: DEFAULT_LOGO_OPACITY,

                      maxHeight: "400px",
                      flexShrink: 1,
                      objectFit: "contain",

                      position: "relative",
                      zIndex: 3,
                    }}
                  />
                </Box>

                {/* LOGO CENTRAL */}

                {logoConfig.center && (
                  <Box
                    sx={{
                      position: "absolute",

                      left: "50%",
                      top: "50%",

                      transform: "translate(-50%, -50%)",

                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",

                      width: centerLogoSizes,

                      maxWidth: {
                        xs: "80px",
                        sm: "100px",
                        md: "120px",
                        lg: "140px",
                        xl: "160px",
                      },

                      zIndex: 3,

                      pointerEvents: "none",
                    }}
                  >
                    <img
                      src={logoConfig.center}
                      alt="Logo central"
                      style={{
                        width: "100%",
                        height: "auto",

                        display: "block",

                        opacity: DEFAULT_LOGO_OPACITY,

                        borderRadius: "8px",

                        maxHeight: "90px",
                        flexShrink: 0,
                        objectFit: "contain",

                        position: "relative",
                        zIndex: 3,
                      }}
                    />
                  </Box>
                )}

                {/* LOGO DERECHO */}

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",

                    width: rightLogoSizes,

                    maxWidth: {
                      xs: "160px",
                      sm: "200px",
                      md: "240px",
                      lg: "350px",
                      xl: "400px",
                    },

                    minWidth: 0,
                    flexShrink: 1,

                    mr: rightLogoMargins,

                    position: "relative",
                    zIndex: 3,
                  }}
                >
                  <img
                    src={logoConfig.right}
                    alt="Logo derecho"
                    style={{
                      width: isFidelitis ? "auto" : "100%",
                      height: "auto",

                      display: "block",

                      opacity: DEFAULT_LOGO_OPACITY,

                      maxHeight: isFidelitis ? "120px" : "110px",
                      flexShrink: 1,
                      objectFit: "contain",

                      borderRadius: "8px",

                      position: "relative",
                      zIndex: 3,
                    }}
                  />
                </Box>
              </>
            );
          })()}
        </Box>
      </Box>

      {/* ======================================================
          CONTRACT
          ====================================================== */}

      <Box
        sx={{
          margin: "-20px 0",
          color: DOCUMENT_FONT_COLOR,
        }}
      >
        <ContractText
          accept={acceptedHtml}
          contract={caseContract}
          includeTitle
        />
      </Box>

      {/* ======================================================
          SIGNATURE TOGGLE
          ====================================================== */}

      <Button
        variant="contained"
        color={showSignature ? "error" : "success"}
        onClick={() => setShowSignature((previous) => !previous)}
        sx={{
          my: 5,
          width: {
            xs: "100%",
            sm: "auto",
          },
        }}
      >
        {showSignature
          ? "Ocultar subir firma"
          : "Subir o dibujar firma (OPCIONAL)"}
      </Button>

      {/* ======================================================
          SIGNATURE
          ====================================================== */}

      <Box
        sx={{
          margin: "20px 0",
          textAlign: "center",
        }}
      >
        <Collapse in={showSignature} timeout={400}>
          <Box>
            <Typography
              component="h3"
              sx={{
                fontFamily: DOCUMENT_FONT,
                fontSize: "18px",
                fontWeight: 600,
                mb: 2,
              }}
            >
              Firma
            </Typography>

            <Card
              sx={{
                maxWidth: 600,
                margin: "0 auto",
                borderRadius: 3,
              }}
            >
              {acceptedHtml && caseContract?.firmaUsuario?.firma_usuario ? (
                <></>
              ) : (
                <Tabs
                  value={signatureMode}
                  onChange={(_, value) => setSignatureMode(value)}
                  centered
                  sx={{
                    borderBottom: "1px solid #ddd",
                  }}
                >
                  <Tab
                    value="canvas"
                    label="Dibujar firma"
                    disabled={!!uploadedSignature}
                  />

                  <Tab
                    value="upload"
                    label="Subir PNG"
                    disabled={!!uploadedSignature}
                  />
                </Tabs>
              )}

              <CardContent>
                {!signatureMode && (
                  <p
                    style={{
                      margin: "20px 0",
                      fontFamily: DOCUMENT_FONT,
                    }}
                  >
                    Seleccione una opción para continuar.
                  </p>
                )}

                {/* DRAW SIGNATURE */}

                {signatureMode === "canvas" && (
                  <>
                    {uploadedSignature ? (
                      <img
                        src={uploadedSignature}
                        alt="Firma del cliente"
                        style={{
                          width: "100%",
                          maxWidth: `${signatureWidth}px`,
                          height: `${signatureHeight}px`,
                          objectFit: "contain",
                          border: "1px solid #ccc",
                          borderRadius: "10px",
                          background: "#fff",
                        }}
                      />
                    ) : (
                      <SignatureCanvas
                        penColor="black"
                        canvasProps={{
                          width: signatureWidth,
                          height: signatureHeight,
                          className: "sigCanvas",
                          style: {
                            width: "100%",
                            maxWidth: `${signatureWidth}px`,
                            border: "1px solid #ccc",
                            borderRadius: "10px",
                            marginTop: "10px",
                          },
                        }}
                        ref={(ref) => setSignaturePad(ref)}
                      />
                    )}

                    {!uploadedSignature && (
                      <Box
                        sx={{
                          marginTop: "15px",
                          display: "flex",
                          justifyContent: "center",
                          gap: "12px",
                          flexWrap: "wrap",
                        }}
                      >
                        <Button
                          variant="outlined"
                          color="warning"
                          onClick={clearSignature}
                          sx={{
                            width: {
                              xs: "100%",
                              sm: "auto",
                            },
                          }}
                        >
                          Limpiar
                        </Button>

                        <Button
                          variant="contained"
                          color="success"
                          onClick={() =>
                            acceptedHtml
                              ? saveSignature("signed")
                              : saveSignature("unsigned")
                          }
                          sx={{
                            width: {
                              xs: "100%",
                              sm: "auto",
                            },
                          }}
                        >
                          Guardar firma
                        </Button>
                      </Box>
                    )}
                  </>
                )}

                {/* UPLOAD SIGNATURE */}

                {signatureMode === "upload" && (
                  <div>
                    <p
                      style={{
                        fontFamily: DOCUMENT_FONT,
                      }}
                    >
                      Cargar archivo de firma (formato PNG)
                    </p>

                    <input
                      id="upload-signature"
                      type="file"
                      accept="image/png"
                      style={{
                        display: "none",
                      }}
                      onChange={(event) => {
                        const file = event.target.files?.[0];

                        if (!file) return;

                        if (file.type !== "image/png") {
                          Swal.fire({
                            title: "Formato incorrecto",
                            text: "Solo se permiten imágenes PNG.",
                            icon: "error",
                            confirmButtonColor: "#1ab394",
                          });

                          return;
                        }

                        if (file.size > 2 * 1024 * 1024) {
                          Swal.fire({
                            title: "Archivo demasiado grande",
                            text: "Máximo 2MB.",
                            icon: "warning",
                            confirmButtonColor: "#1ab394",
                          });

                          return;
                        }

                        const reader = new FileReader();

                        reader.onload = (ev) => {
                          const imgBase64 = ev.target.result;

                          setUploadedSignature(imgBase64);

                          setSignatureImage(imgBase64);

                          if (signaturePad) {
                            signaturePad.clear();
                          }

                          event.target.value = "";
                        };

                        reader.readAsDataURL(file);
                      }}
                    />

                    <Box
                      sx={{
                        marginTop: "10px",
                        display: "flex",
                        justifyContent: "center",
                        gap: "12px",
                        flexWrap: "wrap",
                      }}
                    >
                      <Button
                        variant="outlined"
                        onClick={() =>
                          document.getElementById("upload-signature")?.click()
                        }
                        sx={{
                          width: {
                            xs: "100%",
                            sm: "auto",
                          },
                        }}
                      >
                        Subir archivo PNG
                      </Button>

                      {uploadedSignature && (
                        <Button
                          variant="outlined"
                          color="warning"
                          onClick={() => {
                            setUploadedSignature("");

                            setSignatureImage("");

                            const input =
                              document.getElementById("upload-signature");

                            if (input) {
                              input.value = "";
                            }
                          }}
                          sx={{
                            width: {
                              xs: "100%",
                              sm: "auto",
                            },
                          }}
                        >
                          Quitar imagen
                        </Button>
                      )}
                    </Box>

                    {uploadedSignature && (
                      <img
                        src={uploadedSignature}
                        alt="Firma subida"
                        style={{
                          marginTop: "15px",
                          width: "100%",
                          maxWidth: `${signatureWidth}px`,
                          height: `${signatureHeight}px`,
                          objectFit: "contain",
                          border: "1px solid #ccc",
                          borderRadius: "10px",
                          background: "#fff",
                        }}
                      />
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </Box>
        </Collapse>

        {/* ======================================================
            DOWNLOAD PDF
            ====================================================== */}

        {caseContract?.contractAcceptData?.client_name && (
          <Box
            sx={{
              marginTop: "25px",
              display: "flex",
              justifyContent: {
                xs: "center",
                sm: "flex-end",
              },
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontFamily: DOCUMENT_FONT,
                fontWeight: 600,
                color: "#344054",
              }}
            >
              Tamaño hoja:
            </Typography>

            <ToggleButtonGroup
              exclusive
              size="small"
              value={pdfPaperSize}
              onChange={(_, value) => {
                if (value) {
                  setPdfPaperSize(value);
                }
              }}
              sx={{
                backgroundColor: "#f5f7fa",
                borderRadius: "999px",
                p: "4px",

                "& .MuiToggleButton-root": {
                  border: "none",
                  borderRadius: "999px",
                  px: 2,
                  fontWeight: 600,
                  textTransform: "none",
                  color: "#475467",
                  fontFamily: DOCUMENT_FONT,
                },

                "& .Mui-selected": {
                  backgroundColor: "#1976d2 !important",
                  color: "#fff !important",
                },
              }}
            >
              <ToggleButton value="letter">Carta</ToggleButton>

              <ToggleButton value="legal">Oficio</ToggleButton>
            </ToggleButtonGroup>

            <Button
              variant="contained"
              color="primary"
              onClick={downloadContract}
              startIcon={<DownloadIcon />}
              disabled={!canDownload}
              sx={{
                width: {
                  xs: "100%",
                  sm: "auto",
                },
                fontFamily: DOCUMENT_FONT,
              }}
            >
              Descargar documento completo
            </Button>
          </Box>
        )}
      </Box>

      {/* ======================================================
          ACCEPT / REJECT
          ====================================================== */}

      <FormControl
        component="fieldset"
        sx={{
          textAlign: "center",
          fontFamily: DOCUMENT_FONT,
        }}
      >
        <FormGroup
          aria-label="position"
          row
          sx={{
            alignItems: "center",
            textAlign: "center",
            marginBottom: "10px",
          }}
        >
          {flagAcceptReject && canSign && (
            <Grid container item xs={12}>
              {/* MARKETING */}

              <Grid item xs={12} className="alignItemsAndCenter">
                <FormControlLabel
                  className="formControlLabelCss"
                  control={
                    <Checkbox
                      id="shortSuccess"
                      className="caseCheckBox"
                      checked={marketing}
                    />
                  }
                  label="Acepto el uso de mi correo electrónico para el envío de comunicaciones con fines informativos y comerciales."
                  onChange={() => setMarketing(!marketing)}
                  labelPlacement="end"
                  sx={{
                    textAlign: "left",
                    fontFamily: DOCUMENT_FONT,
                  }}
                />
              </Grid>

              {/* REJECT */}

              <Grid
                item
                xs={12}
                sm={6}
                className="alignItemsAndCenter"
                sx={{
                  paddingRight: "10px",
                }}
              >
                <FormControlLabel
                  className="formControlLabelCss"
                  control={
                    <Checkbox
                      checked={rejectTermsAndConditions}
                      onClick={handleRejectChange}
                      className="caseCheckBox"
                      color="error"
                    />
                  }
                  label="He leído y NO acepto las condiciones"
                  labelPlacement="end"
                  sx={{
                    textAlign: "left",
                    fontFamily: DOCUMENT_FONT,
                  }}
                />

                <Button
                  onClick={handleContractDecision}
                  disabled={acceptTermsAndConditions}
                  variant="contained"
                  color="error"
                  size="small"
                  sx={{
                    width: {
                      xs: "100%",
                      sm: "auto",
                    },
                    fontFamily: DOCUMENT_FONT,
                  }}
                >
                  Rechazar
                </Button>
              </Grid>

              {/* ACCEPT */}

              <Grid
                item
                xs={12}
                sm={6}
                className="alignItemsAndCenter"
                justifyContent="right"
              >
                <FormControlLabel
                  className="formControlLabelCss"
                  control={
                    <Checkbox
                      checked={acceptTermsAndConditions}
                      color="success"
                      onClick={handleAcceptChange}
                      className="caseCheckBox"
                    />
                  }
                  label="He leído y acepto las condiciones"
                  labelPlacement="end"
                  sx={{
                    textAlign: "left",
                    fontFamily: DOCUMENT_FONT,
                  }}
                />

                {/* COMPLETE PENDING TASK */}

                {shouldShowCompletePendingTaskButton &&
                  can("cases", "edit") && (
                    <Box
                      sx={{
                        mb: 2,
                        textAlign: "end",
                      }}
                    >
                      <CustomButtonPlusIcon
                        icon={ArrowForwardIos}
                        text="Dar por completada la tarea"
                        handleFunction={handleCompletePendingTask}
                        position="end"
                        variant="contained"
                        color="success"
                      />
                    </Box>
                  )}

                <Button
                  onClick={handleContractDecision}
                  disabled={rejectTermsAndConditions}
                  variant="contained"
                  color="success"
                  size="small"
                  sx={{
                    width: {
                      xs: "100%",
                      sm: "auto",
                    },
                    fontFamily: DOCUMENT_FONT,
                  }}
                >
                  Aceptar
                </Button>
              </Grid>
            </Grid>
          )}
        </FormGroup>
      </FormControl>

      {/* ======================================================
          OTP DIALOG
          ====================================================== */}

      <Dialog
        open={openOtpDialog}
        onClose={handleCloseOtpDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontFamily: DOCUMENT_FONT,
          }}
        >
          Reenviar código
        </DialogTitle>

        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Correo electrónico del cliente"
            type="email"
            fullWidth
            size="small"
            disabled
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            sx={{
              "& .MuiInputBase-root": {
                fontFamily: DOCUMENT_FONT,
              },

              "& .MuiInputLabel-root": {
                fontFamily: DOCUMENT_FONT,
              },
            }}
          />
        </DialogContent>

        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Button
            onClick={handleCloseOtpDialog}
            color="error"
            size="small"
            sx={{
              fontFamily: DOCUMENT_FONT,
            }}
          >
            Cancelar
          </Button>

          <Button
            onClick={handleSend}
            variant="contained"
            color="primary"
            size="small"
            disabled={!email}
            sx={{
              fontFamily: DOCUMENT_FONT,
            }}
          >
            Reenviar código
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FirmaContent;
